<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('QUEUES_MGMT.SUPERVISOR.CHANGE_QUEUE.TITLE')"
        :header-content="
          $t('QUEUES_MGMT.SUPERVISOR.CHANGE_QUEUE.SUB_TEXT', {
            id: conversationIds.join(', #'),
          })
        "
      />
      <div class="p-8 pt-4">
        <!-- QM §5.10 — multiselect -->
        <label>
          {{ $t('QUEUES_MGMT.SUPERVISOR.CHANGE_QUEUE.TARGET_LABEL') }}
        </label>
        <multiselect
          v-model="selectedQueue"
          :options="targetQueues"
          track-by="id"
          label="name"
          :close-on-select="true"
          :allow-empty="false"
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
          :placeholder="$t('QUEUES_MGMT.SUPERVISOR.CHANGE_QUEUE.TARGET_LABEL')"
        />

        <div class="flex justify-end items-center py-2 px-0 gap-2 w-full">
          <woot-button class="button clear" @click.prevent="onClose">
            {{ $t('QUEUES_MGMT.SUPERVISOR.CHANGE_QUEUE.CANCEL') }}
          </woot-button>
          <woot-button
            :is-disabled="!selectedQueue || isSubmitting"
            :is-loading="isSubmitting"
            @click="submit"
          >
            {{ $t('QUEUES_MGMT.SUPERVISOR.CHANGE_QUEUE.CONFIRM') }}
          </woot-button>
        </div>
      </div>
    </div>

    <!-- QM §4.13.3 — a locked ticket is refused until the supervisor overrides. -->
    <woot-confirm-modal
      ref="lockConfirm"
      :title="$t('QUEUES_MGMT.SUPERVISOR.LOCK_OVERRIDE.TITLE')"
      :description="$t('QUEUES_MGMT.SUPERVISOR.LOCK_OVERRIDE.DESC')"
      :confirm-label="$t('QUEUES_MGMT.SUPERVISOR.LOCK_OVERRIDE.YES')"
      :cancel-label="$t('QUEUES_MGMT.SUPERVISOR.LOCK_OVERRIDE.NO')"
    />
  </woot-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import QueueActionsAPI from 'dashboard/api/queueActions';

export default {
  mixins: [alertMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    // QM §5.5.2 display_ids of the tickets being moved — one, or several via bulk select.
    conversationIds: {
      type: Array,
      required: true,
    },
    currentQueueId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      selectedQueue: null,
      isSubmitting: false,
    };
  },
  computed: {
    ...mapGetters({
      queues: 'queues/getQueues',
    }),
    targetQueues() {
      return this.queues.filter(queue => queue.id !== this.currentQueueId);
    },
  },
  watch: {
    show(isVisible) {
      if (isVisible) {
        this.selectedQueue = null;
      }
    },
  },
  methods: {
    // QM §5.5.2 — one request per ticket so each move is audited individually.
    async submit() {
      this.isSubmitting = true;
      const moved = [];
      const locked = [];
      let failed = 0;
      for (const conversationId of this.conversationIds) {
        // eslint-disable-next-line no-await-in-loop
        const outcome = await this.moveOne(conversationId, false);
        if (outcome === 'ok') moved.push(conversationId);
        else if (outcome === 'locked') locked.push(conversationId);
        else failed += 1;
      }
      this.isSubmitting = false;
      await this.finish(moved, locked, failed);
    },
    // Returns 'ok' | 'locked' | 'error'. 422 means the ticket is locked (§4.13.3).
    async moveOne(conversationId, overrideLock) {
      try {
        await QueueActionsAPI.changeQueue(
          conversationId,
          this.selectedQueue.id,
          overrideLock
        );
        return 'ok';
      } catch (error) {
        return error?.response?.status === 422 ? 'locked' : 'error';
      }
    },
    async finish(moved, locked, failed) {
      // A locked ticket is recoverable: ask once, then retry the locked ones only.
      if (locked.length) {
        const confirmed = await this.$refs.lockConfirm.showConfirmation();
        if (confirmed) {
          this.isSubmitting = true;
          for (const conversationId of locked) {
            // eslint-disable-next-line no-await-in-loop
            const outcome = await this.moveOne(conversationId, true);
            if (outcome === 'ok') moved.push(conversationId);
            else failed += 1;
          }
          this.isSubmitting = false;
        } else {
          failed += locked.length;
        }
      }
      this.report(moved, failed);
    },
    report(moved, failed) {
      if (moved.length) {
        this.showAlert(
          this.$t('QUEUES_MGMT.SUPERVISOR.TOAST.QUEUE_CHANGED', {
            id: moved.join(', #'),
          })
        );
        this.$emit('changed', moved);
      }
      if (failed) {
        this.showAlert(
          this.$t('QUEUES_MGMT.SUPERVISOR.TOAST.BULK_FAILED', { count: failed })
        );
      }
      this.onClose();
    },
    onClose() {
      this.$emit('close');
    },
  },
};
</script>
